import type {
  GoodsColor,
  GoodsDisplay,
  GoodsColumn,
  Hex
} from '../../types';
import { debug } from '../../utils/debug';

// ============================================================================
// 汎用設定インターフェース
// ============================================================================

/**
 * 商品キューブの設定（マップ固有）
 */
export interface GoodsCubesConfig {
  /** 各色のキューブ数 */
  cubesPerColor: Partial<Record<GoodsColor, number>>;
  /** 除外する色（バリアントルール対応） */
  excludeColors?: GoodsColor[];
}

/**
 * 商品補充表の設定（マップ固有）
 */
export interface GoodsDisplayConfig {
  /** 明側の初期配置数（最上段に配置するキューブ数） */
  lightSideInitialCount: number;
  /** 暗側を使用するか */
  darkSideEnabled: boolean;
  /** 明側の都市ID配列（列1-6に対応） */
  lightSideCityIds: string[];
  /** 暗側の都市ID配列（列1-6に対応、Detroit等で使用） */
  darkSideCityIds?: string[];
  /** 明側の新都市配置（列番号と新都市IDのペア） */
  lightSideNewCities: Array<{ columnNumber: number; newCityId: string }>;
  /** 暗側の新都市配置（列番号と新都市IDのペア） */
  darkSideNewCities: Array<{ columnNumber: number; newCityId: string }>;
  /** 色制限ルール（特定位置に配置禁止の色） */
  colorRestrictions?: Array<{ position: number; excludeColor: GoodsColor }>;
}

/**
 * マップ初期配置の設定
 */
export interface InitialPlacementConfig {
  /** 初期キューブを配置する都市の色（nullは全都市） */
  targetCityColor: GoodsColor | null;
  /** 各都市に配置するキューブ数 */
  cubesPerCity: number;
}

// ============================================================================
// Barbados 固有設定
// ============================================================================

/**
 * Barbados 商品キューブ設定
 */
export const BARBADOS_CUBES_CONFIG: GoodsCubesConfig = {
  cubesPerColor: {
    red: 20,
    blue: 20,
    yellow: 20,
    black: 16
    // 紫は除外
  },
  excludeColors: ['purple']
};

/**
 * Barbados 商品補充表設定
 */
export const BARBADOS_DISPLAY_CONFIG: GoodsDisplayConfig = {
  lightSideInitialCount: 10,
  darkSideEnabled: false,
  lightSideCityIds: [
    'NORTH_POINT',    // 列1
    'BATHSHEBA',      // 列2
    'BELL_POINT',     // 列3
    'BRIDGETOWN',     // 列4
    'OISTINS',        // 列5
    'CRANE_BEACH'     // 列6
  ],
  lightSideNewCities: [
    { columnNumber: 3, newCityId: 'A' },
    { columnNumber: 4, newCityId: 'B' },
    { columnNumber: 5, newCityId: 'C' },
    { columnNumber: 6, newCityId: 'D' }
  ],
  darkSideNewCities: [
    { columnNumber: 1, newCityId: 'E' },
    { columnNumber: 2, newCityId: 'F' },
    { columnNumber: 3, newCityId: 'G' },
    { columnNumber: 4, newCityId: 'H' }
  ],
  colorRestrictions: [
    { position: 6, excludeColor: 'red' },   // 新都市Aに赤禁止
    { position: 7, excludeColor: 'blue' }   // 新都市Bに青禁止
  ]
};

/**
 * Barbados 初期配置設定
 */
export const BARBADOS_PLACEMENT_CONFIG: InitialPlacementConfig = {
  targetCityColor: 'yellow',  // 黄色の都市（全6都市）
  cubesPerCity: 1
};

// ============================================================================
// 汎用関数
// ============================================================================

/**
 * 商品キューブ袋の初期化（汎用）
 *
 * @param config - 商品キューブ設定
 * @returns 商品キューブの配列（シャッフル済み）
 */
export function initializeCubes(config: GoodsCubesConfig): GoodsColor[] {
  const cubes: GoodsColor[] = [];

  for (const [color, count] of Object.entries(config.cubesPerColor)) {
    if (count && count > 0) {
      const goodsColor = color as GoodsColor;
      if (!config.excludeColors?.includes(goodsColor)) {
        cubes.push(...Array(count).fill(goodsColor));
      }
    }
  }

  shuffleArray(cubes);
  return cubes;
}

/**
 * 商品補充表の初期化（汎用）
 *
 * @param cubesInBag - 袋に入っている商品キューブ
 * @param config - 商品補充表設定
 * @returns 初期化された商品補充表と更新された袋の内容
 */
export function initializeGoodsDisplay(
  cubesInBag: GoodsColor[],
  config: GoodsDisplayConfig
): { goodsDisplay: GoodsDisplay; remainingCubes: GoodsColor[] } {
  // 1. 袋をシャッフル
  const shuffled = [...cubesInBag];
  shuffleArray(shuffled);

  // 2. ランダムに必要数抽出
  const selected = shuffled.slice(0, config.lightSideInitialCount);

  // 3. 色制限チェック
  if (config.colorRestrictions) {
    for (const restriction of config.colorRestrictions) {
      if (selected[restriction.position] === restriction.excludeColor) {
        const replacement = findReplacementCube(
          shuffled.slice(config.lightSideInitialCount),
          restriction.excludeColor
        );
        if (replacement) {
          selected[restriction.position] = replacement;
        }
      }
    }
  }

  // 4. 商品補充表を構築
  const lightSide: GoodsColumn[] = [];
  const darkSide: GoodsColumn[] = [];

  // 明側: 都市列 1-6（各3行）
  for (let col = 1; col <= 6; col++) {
    lightSide.push({
      columnNumber: col,
      cityId: config.lightSideCityIds[col - 1] || '',
      newCityId: null,
      cubes: [
        selected[col - 1] || null, // 最上段のみ配置
        null,
        null
      ]
    });
  }

  // 明側: 新都市列
  for (let i = 0; i < config.lightSideNewCities.length; i++) {
    const newCity = config.lightSideNewCities[i];
    lightSide.push({
      columnNumber: newCity.columnNumber,
      cityId: '',
      newCityId: newCity.newCityId,
      cubes: [
        selected[6 + i] || null,
        null
      ]
    });
  }

  // 暗側: 都市列 1-6（各3行）
  for (let col = 1; col <= 6; col++) {
    darkSide.push({
      columnNumber: col,
      cityId: config.darkSideCityIds ? config.darkSideCityIds[col - 1] || '' : '', // 暗側の都市は設定で指定可能
      newCityId: null,
      cubes: [null, null, null]
    });
  }

  // 暗側: 新都市列
  for (const newCity of config.darkSideNewCities) {
    darkSide.push({
      columnNumber: newCity.columnNumber,
      cityId: '',
      newCityId: config.darkSideEnabled ? newCity.newCityId : null,
      cubes: [null, null]
    });
  }

  const goodsDisplay: GoodsDisplay = {
    lightSide,
    darkSide
  };

  // 5. 残りのキューブを袋に戻す
  const remainingCubes = shuffled.slice(config.lightSideInitialCount);

  return { goodsDisplay, remainingCubes };
}

/**
 * マップ上の都市に初期商品を配置（汎用）
 *
 * @param map - マップ全体（Hex配列）
 * @param cubesInBag - 袋に入っている商品キューブ
 * @param config - 初期配置設定
 * @returns 更新されたマップと袋の内容
 */
export function placeInitialGoodsOnMap(
  map: Hex[],
  cubesInBag: GoodsColor[],
  config: InitialPlacementConfig = BARBADOS_PLACEMENT_CONFIG
): { updatedMap: Hex[]; remainingCubes: GoodsColor[] } {
  const updatedMap = [...map];
  const remainingCubes = [...cubesInBag];

  // 対象都市をフィルタ
  // 都市の color は単一値（GoodsColor）または配列（GoodsColor[]）の場合がある
  const targetCities = config.targetCityColor
    ? updatedMap.filter(hex => {
        if (hex.city === null) return false;
        const cityColor = hex.city.color;
        if (Array.isArray(cityColor)) {
          return cityColor.includes(config.targetCityColor!);
        }
        return cityColor === config.targetCityColor;
      })
    : updatedMap.filter(hex => hex.city !== null);

  targetCities.forEach(hex => {
    for (let i = 0; i < config.cubesPerCity; i++) {
      if (remainingCubes.length === 0) {
        console.warn('[placeInitialGoodsOnMap] No cubes left in bag');
        return;
      }

      // ランダムに1個抽出
      const randomIndex = Math.floor(Math.random() * remainingCubes.length);
      const cube = remainingCubes.splice(randomIndex, 1)[0];

      // 都市に配置
      hex.goods.push(cube);
    }
  });

  return { updatedMap, remainingCubes };
}

// ============================================================================
// Barbados 専用関数（後方互換）
// ============================================================================

/**
 * Barbados 商品補充表の初期化
 *
 * @param cubesInBag - 袋に入っている商品キューブ（紫除外済み）
 * @returns 初期化された商品補充表と更新された袋の内容
 */
export function initializeBarbadosGoodsDisplay(
  cubesInBag: GoodsColor[]
): { goodsDisplay: GoodsDisplay; remainingCubes: GoodsColor[] } {
  return initializeGoodsDisplay(cubesInBag, BARBADOS_DISPLAY_CONFIG);
}

/**
 * 商品キューブ袋の初期化（Barbados: 紫を除外）
 *
 * @returns 商品キューブの配列（シャッフル済み）
 */
export function initializeBarbadosCubes(): GoodsColor[] {
  return initializeCubes(BARBADOS_CUBES_CONFIG);
}

// ============================================================================
// Puerto Rico 専用関数
// ============================================================================

/**
 * Puerto Rico 商品キューブの初期化（難易度依存）
 *
 * @param redCount - 赤キューブ数
 * @param blackCount - 黒キューブ数
 * @returns 商品キューブの配列（シャッフル済み）
 */
export function initializePuertoRicoCubes(
  redCount: number,
  blackCount: number
): GoodsColor[] {
  const cubes: GoodsColor[] = [
    ...Array(redCount).fill('red' as GoodsColor),
    ...Array(blackCount).fill('black' as GoodsColor)
  ];

  shuffleArray(cubes);
  return cubes;
}

/**
 * Puerto Rico 町への初期商品配置
 * 各町（10町）に2個ずつランダム配置、都市（SAN JUAN）には配置しない
 *
 * @param map - マップ全体（Hex配列）
 * @param cubesInBag - 袋に入っている商品キューブ（22個想定）
 * @returns 更新されたマップと袋に残るキューブ（2個想定）
 */
export function placePuertoRicoInitialGoods(
  map: Hex[],
  cubesInBag: GoodsColor[]
): { updatedMap: Hex[]; remainingCubes: GoodsColor[] } {
  const updatedMap = [...map];
  const remainingCubes = [...cubesInBag];

  // Puerto Rico の町リスト（11町）
  const townNames = [
    'LUQUILLO', 'HUMACAO', 'CAGUAS', 'BAYAMON', 'PONCE',
    'ARECIBO', 'UTUADO', 'AGUADILLA', 'MAYAGUEZ', 'CABO_RJO', 'CAYEY'
  ];

  // 各町を取得
  const targetTowns = updatedMap.filter(hex => {
    if (!hex.town) return false;
    // town.id は "CABO_RJO" のように空白が _ に置換されている
    const townId = hex.town.id;
    return townNames.includes(townId);
  });

  if (targetTowns.length !== 11) {
    console.warn(`[placePuertoRicoInitialGoods] Expected 11 towns, found ${targetTowns.length}`);
  }

  // 各町に2個ずつ配置
  targetTowns.forEach(hex => {
    for (let i = 0; i < 2; i++) {
      if (remainingCubes.length === 0) {
        console.warn('[placePuertoRicoInitialGoods] No cubes left in bag');
        return;
      }

      // 先頭から1個取り出し（既にシャッフル済み）
      const cube = remainingCubes.shift()!;
      hex.goods.push(cube);
    }
  });

  // 残り2個が袋に残る
  debug.log(`[placePuertoRicoInitialGoods] Placed ${20 - remainingCubes.length} cubes on map, ${remainingCubes.length} cubes remain in bag`);

  return { updatedMap, remainingCubes };
}

// ============================================================================
// ユーティリティ関数
// ============================================================================

/**
 * 配列をシャッフル（Fisher-Yates）
 */
function shuffleArray<T>(array: T[]): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

/**
 * 指定色以外の商品キューブを検索
 */
function findReplacementCube(
  cubes: GoodsColor[],
  excludeColor: GoodsColor
): GoodsColor | null {
  const candidate = cubes.find(c => c !== excludeColor);
  return candidate || null;
}

// ============================================================================
// Detroit Bankruptcy Solo 専用関数
// ============================================================================

/**
 * Detroit 商品キューブ設定
 * - 全5色使用（各20個）
 */
export const DETROIT_CUBES_CONFIG: GoodsCubesConfig = {
  cubesPerColor: {
    red: 20,
    blue: 20,
    purple: 20,
    yellow: 20,
    black: 20
  },
  excludeColors: []
};

/**
 * Detroit 商品補充表設定
 * - 両側（明/暗）使用
 * - 最上段に計14個配置
 */
export const DETROIT_DISPLAY_CONFIG: GoodsDisplayConfig = {
  lightSideInitialCount: 10,  // 明側: 都市6列 + 新都市4列
  darkSideEnabled: true,
  lightSideCityIds: [
    'YPSILANTI',       // 列1 (white)
    'NOVI',            // 列2 (white)
    'MILFORD',         // 列3 (white)
    'PONTIAC',         // 列4 (white)
    'UTICA',           // 列5 (white)
    'WINDSOR'          // 列6 (white)
  ],
  darkSideCityIds: [
    'DETROIT_METRO',      // 列1 (black)
    'DEARBORN',           // 列2 (black)
    'SOUTHFIELD',         // 列3 (black)
    'ST._CLAIR_SHORES',   // 列4 (black)
    'MIDTOWN_DETROIT',    // 列5 (black)
    'DOWNTOWN_DETROIT'    // 列6 (black)
  ],
  lightSideNewCities: [
    { columnNumber: 3, newCityId: 'A' },
    { columnNumber: 4, newCityId: 'B' },
    { columnNumber: 5, newCityId: 'C' },
    { columnNumber: 6, newCityId: 'D' }
  ],
  darkSideNewCities: [
    { columnNumber: 1, newCityId: 'E' },
    { columnNumber: 2, newCityId: 'F' },
    { columnNumber: 3, newCityId: 'G' },
    { columnNumber: 4, newCityId: 'H' }
  ]
};

/**
 * Detroit 初期配置設定
 * - 全都市に2個ずつ配置
 */
export const DETROIT_PLACEMENT_CONFIG: InitialPlacementConfig = {
  targetCityColor: null,
  cubesPerCity: 2
};

/**
 * Detroit 商品キューブの初期化
 *
 * @returns 商品キューブの配列（シャッフル済み）
 */
export function initializeDetroitCubes(): GoodsColor[] {
  return initializeCubes(DETROIT_CUBES_CONFIG);
}

/**
 * Detroit 商品補充表の初期化
 * 両側（明/暗）の最上段にランダムにキューブを配置（計14個）
 *
 * @param cubesInBag - 袋に入っている商品キューブ
 * @returns 初期化された商品補充表と更新された袋の内容
 */
export function initializeDetroitGoodsDisplay(
  cubesInBag: GoodsColor[]
): { goodsDisplay: GoodsDisplay; remainingCubes: GoodsColor[] } {
  // 1. 袋をシャッフル
  const shuffled = [...cubesInBag];
  shuffleArrayPublic(shuffled);

  // 必要数: 両側（明/暗）の都市列1-6 + 新都市（A-D, E-H）
  // 明側: 都市6列 + 新都市4列 = 10個
  // 暗側: 都市6列 + 新都市4列 = 10個
  // 合計: 20個
  const lightSideCubeCount = 6 + 4;  // 都市6列 + 新都市4列 = 10個
  const darkSideCubeCount = 6 + 4;   // 都市6列 + 新都市4列 = 10個
  const totalInitialCount = lightSideCubeCount + darkSideCubeCount;  // 20個

  const selected = shuffled.slice(0, totalInitialCount);

  // 2. 商品補充表を構築
  const lightSide: GoodsColumn[] = [];
  const darkSide: GoodsColumn[] = [];

  // 明側: 都市列 1-6（各3行）- 最上段に配置
  for (let col = 1; col <= 6; col++) {
    lightSide.push({
      columnNumber: col,
      cityId: DETROIT_DISPLAY_CONFIG.lightSideCityIds[col - 1] || '',
      newCityId: null,
      cubes: [
        selected[col - 1] || null, // 最上段のみ配置
        null,
        null
      ]
    });
  }

  // 明側: 新都市列（A-D）- 最上段に配置
  for (let i = 0; i < DETROIT_DISPLAY_CONFIG.lightSideNewCities.length; i++) {
    const newCity = DETROIT_DISPLAY_CONFIG.lightSideNewCities[i];
    lightSide.push({
      columnNumber: newCity.columnNumber,
      cityId: '',
      newCityId: newCity.newCityId,
      cubes: [
        selected[6 + i] || null,  // 最上段のみ配置
        null
      ]
    });
  }

  // 暗側: 都市列 1-6（各3行）- 最上段に配置
  for (let col = 1; col <= 6; col++) {
    darkSide.push({
      columnNumber: col,
      cityId: DETROIT_DISPLAY_CONFIG.darkSideCityIds![col - 1] || '',
      newCityId: null,
      cubes: [
        selected[10 + col - 1] || null,  // 最上段のみ配置（index 10-15）
        null,
        null
      ]
    });
  }

  // 暗側: 新都市列（E-H）- 最上段に配置
  for (let i = 0; i < DETROIT_DISPLAY_CONFIG.darkSideNewCities.length; i++) {
    const newCity = DETROIT_DISPLAY_CONFIG.darkSideNewCities[i];
    darkSide.push({
      columnNumber: newCity.columnNumber,
      cityId: '',
      newCityId: newCity.newCityId,
      cubes: [
        selected[16 + i] || null,  // 最上段のみ配置（index 16-19）
        null
      ]
    });
  }

  const goodsDisplay: GoodsDisplay = {
    lightSide,
    darkSide
  };

  // 3. 残りのキューブを袋に戻す
  const remainingCubes = shuffled.slice(totalInitialCount);

  debug.log(`[initializeDetroitGoodsDisplay] Initialized with ${totalInitialCount} cubes on display, ${remainingCubes.length} remaining in bag`);

  return { goodsDisplay, remainingCubes };
}

/**
 * Detroit マップ初期商品配置
 * 各都市に2個ずつランダム配置
 *
 * @param map - マップ全体（Hex配列）
 * @param cubesInBag - 袋に入っている商品キューブ
 * @returns 更新されたマップと袋に残るキューブ
 */
export function placeDetroitInitialGoods(
  map: Hex[],
  cubesInBag: GoodsColor[]
): { updatedMap: Hex[]; remainingCubes: GoodsColor[] } {
  return placeInitialGoodsOnMap(map, cubesInBag, DETROIT_PLACEMENT_CONFIG);
}

/**
 * 配列をシャッフル（Fisher-Yates）- 公開版
 */
function shuffleArrayPublic<T>(array: T[]): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

// ============================================================================
// Seattle Metro Solo 専用関数
// ============================================================================

/**
 * Seattle 商品キューブ設定
 * - 全5色使用（各20個）
 */
export const SEATTLE_CUBES_CONFIG: GoodsCubesConfig = {
  cubesPerColor: {
    red: 20,
    blue: 20,
    purple: 20,
    yellow: 20,
    black: 20
  },
  excludeColors: []
};

/**
 * Seattle 商品キューブの初期化
 *
 * @returns 商品キューブの配列（シャッフル済み）
 */
export function initializeSeattleCubes(): GoodsColor[] {
  return initializeCubes(SEATTLE_CUBES_CONFIG);
}

/**
 * Seattle 町リスト（10町）
 */
const SEATTLE_TOWNS = [
  'CROWN_HILL',
  'WALLINGFORD',
  'FREMONT',
  'DISCOVERY_PARK',
  'ISLAND_CREST',
  'CAPITOL_HILL',
  'SEATTLE_CENTER',
  'ALKI_BEACH',
  'COLUMBIA_CITY',
  'RENTON'
];

/**
 * Seattle Town への初期商品配置
 * 10の Town にランダムに1個ずつ配置
 *
 * @param map - マップ全体（Hex配列）
 * @param cubesInBag - 袋に入っている商品キューブ
 * @returns 更新されたマップと袋に残るキューブ
 */
export function placeSeattleInitialGoods(
  map: Hex[],
  cubesInBag: GoodsColor[]
): { updatedMap: Hex[]; remainingCubes: GoodsColor[] } {
  const updatedMap = [...map];
  const remainingCubes = [...cubesInBag];

  // 町を取得（10町）
  const targetTowns = updatedMap.filter(hex => {
    if (!hex.town) return false;
    return SEATTLE_TOWNS.includes(hex.town.id);
  });

  if (targetTowns.length !== 10) {
    console.warn(`[placeSeattleInitialGoods] Expected 10 towns, found ${targetTowns.length}`);
  }

  // 各町に1個ずつ配置
  targetTowns.forEach(hex => {
    if (remainingCubes.length === 0) {
      console.warn('[placeSeattleInitialGoods] No cubes left in bag');
      return;
    }

    // ランダムに1個抽出
    const randomIndex = Math.floor(Math.random() * remainingCubes.length);
    const cube = remainingCubes.splice(randomIndex, 1)[0];
    hex.goods.push(cube);
  });

  debug.log(`[placeSeattleInitialGoods] Placed ${10 - (remainingCubes.length - (cubesInBag.length - 10))} cubes on map, ${remainingCubes.length} cubes remain in bag`);

  return { updatedMap, remainingCubes };
}
